import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { lineMath } from './ItemsGrid.jsx';

const PAYMENT_METHODS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'credit', label: 'آجل' },
  { key: 'partial', label: 'جزئي' }
];

export function TotalsSidebar({
  items = [],
  discount,
  onDiscountChange,
  paymentMethod,
  onPaymentMethodChange,
  paid,
  onPaidChange,
  cashboxId,
  onCashboxChange,
  cashboxes = [],
  cashboxesLoading,
  updateCost = false,
  onUpdateCostChange,
  // سياسة كلفة المنتج من إعدادات النظام: last = الكلفة الأخيرة المدخلة (الافتراضي)،
  // weighted = المتوسط المرجّح. تُعرَض بجانب الخيار حتى يعرف المستخدم ما سيحدث.
  costPolicy = 'last',
  updateSalePrice = false,
  onUpdateSalePriceChange,
  readOnly,
  onSaveDraft,
  onSaveAndPost,
  onCancel,
  saving,
  posting,
  isEdit = false
}) {
  // المجموع الفرعي يبقى «قبل الخصم»، وخصومات المنتجات تُعرض سطراً مستقلاً
  // ثم خصم الفاتورة فوقها — نفس ترتيب الخادم تماماً فلا يختلف رقمان.
  const lines = items.map((it) => lineMath(it));
  const subtotal = lines.reduce((s, l) => s + l.grossTotal, 0);
  const itemsDiscount = lines.reduce((s, l) => s + l.discountValue, 0);
  const afterItems = Math.max(0, subtotal - itemsDiscount);
  const discountVal = Number(discount) || 0;
  const total = Math.max(0, afterItems - discountVal);
  const headDiscountTooBig = discountVal > afterItems;
  const lineInvalid = lines.some((l) => l.invalid);
  const paidVal = Math.max(0, Number(paid) || 0);
  const due = total - paidVal;

  // For cash: paid must equal total. For credit: paid = 0. For partial: paid > 0 && < total.
  const paidValid = paymentMethod === 'cash' ? paidVal === total
    : paymentMethod === 'credit' ? paidVal === 0
    : paidVal > 0 && paidVal < total;

  // لماذا لا يصحّ المبلغ — زرّ معطّل بلا سبب ظاهر كان يُربك المستخدم.
  const paidHint = paidValid ? null
    : paymentMethod === 'cash' ? 'الدفع نقداً يتطلب تسديد كامل المبلغ — أو اختر «جزئي».'
    : paymentMethod === 'credit' ? 'الدفع آجل لا يقبل مبلغاً مدفوعاً.'
    : 'الدفع الجزئي يتطلب مبلغاً أكبر من صفر وأقل من الإجمالي.';

  // النقد يخرج من قاصة محددة، ولا يجوز أن يتجاوز رصيدها.
  const needsCashbox = paymentMethod === 'cash' || paymentMethod === 'partial';
  const selectedBox = cashboxes.find((cb) => String(cb._id || cb.id) === String(cashboxId || ''));
  const boxBalance = selectedBox ? Number(selectedBox.currentBalance || 0) : 0;
  const cashboxMissing = needsCashbox && !cashboxId;
  const insufficient = needsCashbox && !!selectedBox && paidVal > boxBalance;
  const canPost = paidValid && !cashboxMissing && !insufficient && !headDiscountTooBig && !lineInvalid;

  return (
    <div className="card sticky top-[88px] self-start">
      <div className="card-h">
        <h3 className="text-[14.5px] font-bold flex-1">الإجمالي والدفع</h3>
      </div>
      <div className="card-b space-y-2">
        <div className="flex justify-between text-[13px]">
          <span className="text-ink-3">عدد الأصناف</span>
          <b>{items.length}</b>
        </div>
        <div className="flex justify-between text-[13px]">
          <span className="text-ink-3">إجمالي الكميات</span>
          <b>{items.reduce((s, it) => s + (Number(it.qty) || 0), 0)}</b>
        </div>
        <div className="flex justify-between text-[13px]">
          <span className="text-ink-3">المجموع الفرعي (قبل الخصم)</span>
          <b>{formatIQD(subtotal)}</b>
        </div>
        {itemsDiscount > 0 ? (
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-3">خصومات المنتجات</span>
            <b style={{ color: '#B45309' }}>- {formatIQD(itemsDiscount)}</b>
          </div>
        ) : null}
        {itemsDiscount > 0 ? (
          <div className="flex justify-between text-[13px]">
            <span className="text-ink-3">بعد خصومات المنتجات</span>
            <b>{formatIQD(afterItems)}</b>
          </div>
        ) : null}

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">خصم على الفاتورة</label>
          <input
            type="number"
            min="0"
            className={clsx('input text-[13px]', headDiscountTooBig && 'ring-1 ring-red')}
            value={discount}
            readOnly={readOnly}
            onChange={(e) => onDiscountChange(Number(e.target.value) || 0)}
          />
          {headDiscountTooBig ? (
            <div className="text-[11.5px] text-red mt-1 font-semibold">
              خصم الفاتورة أكبر من إجمالي الأصناف بعد خصومات المنتجات.
            </div>
          ) : null}
        </div>

        <div className="border-t border-line pt-2 flex justify-between items-center">
          <b className="text-[13px]">الإجمالي النهائي</b>
          <b className="text-[20px] text-orange-deep">{formatIQD(total)}</b>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع</label>
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m.key}
                type="button"
                disabled={readOnly}
                /* تعبئة المبلغ المدفوع تُدار في الشاشة الأمّ (تتبع الإجمالي نقداً). */
                onClick={() => onPaymentMethodChange(m.key)}
                className={clsx(
                  'flex-1 px-2 py-1.5 rounded-[8px] text-[12px] font-semibold',
                  paymentMethod === m.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ المدفوع</label>
          <input
            type="number"
            min="0"
            className={clsx('input', !paidValid && !readOnly && 'ring-1 ring-red')}
            style={{ fontWeight: 700, fontSize: 16 }}
            value={paid}
            readOnly={readOnly || paymentMethod === 'credit'}
            onChange={(e) => onPaidChange(Number(e.target.value) || 0)}
          />
          {paidHint && !readOnly ? (
            <div className="text-[11.5px] text-red mt-1 font-semibold">{paidHint}</div>
          ) : null}
          {paymentMethod === 'cash' && paidValid && !readOnly ? (
            <div className="text-[11px] text-ink-3 mt-1">يتبع الإجمالي تلقائياً. اكتب رقماً لتثبيت مبلغ مختلف.</div>
          ) : null}
        </div>

        {needsCashbox ? (
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              القاصة التي يُدفع منها <span className="text-red">*</span>
            </label>
            <select
              className={clsx('input', (cashboxMissing || insufficient) && 'ring-1 ring-red')}
              value={cashboxId || ''}
              disabled={readOnly}
              onChange={(e) => onCashboxChange(e.target.value || null)}
            >
              <option value="">— اختر القاصة —</option>
              {cashboxes.map((cb) => {
                const id = String(cb._id || cb.id);
                return (
                  <option key={id} value={id}>
                    {cb.name} — {formatIQD(cb.currentBalance || 0)}
                  </option>
                );
              })}
            </select>
            {cashboxesLoading ? (
              <div className="text-[11px] text-ink-3 mt-1">جاري تحميل القاصات...</div>
            ) : !cashboxes.length ? (
              <div className="text-[11px] text-red mt-1">لا توجد قاصات نشطة — أضف قاصة أولاً.</div>
            ) : selectedBox ? (
              <div className="flex justify-between text-[11.5px] mt-1">
                <span className="text-ink-3">الرصيد بعد الدفع</span>
                <b style={{ color: insufficient ? '#B91C1C' : '#15803D' }}>
                  {formatIQD(boxBalance - paidVal)}
                </b>
              </div>
            ) : null}
            {insufficient ? (
              <div className="text-[11.5px] text-red mt-1 font-semibold">
                لا يمكن إتمام عملية الشراء لعدم توفر رصيد كافٍ في القاصة
                ({formatIQD(boxBalance)} متوفر، والمطلوب {formatIQD(paidVal)}).
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          className={clsx(
            'flex justify-between items-center px-3 py-2 rounded-[10px]',
            due > 0 ? 'bg-[#FEF2F2]' : 'bg-[#DCFCE7]'
          )}
        >
          <span className={clsx('font-bold', due > 0 ? 'text-[#B91C1C]' : 'text-[#15803D]')}>
            {due > 0 ? 'متبقي للمورد' : 'مكتمل'}
          </span>
          <b className={clsx(due > 0 ? 'text-[#B91C1C]' : 'text-[#15803D]')}>
            {formatIQD(due)}
          </b>
        </div>

        {!readOnly ? (
          <div className="bg-paper-2 rounded-[10px] p-2.5 space-y-1.5">
            <div className="text-[11.5px] font-bold text-ink-2">أسعار المنتج الافتراضية</div>
            <label className="flex items-start gap-2 text-[11.5px] text-ink-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!updateCost}
                onChange={(e) => onUpdateCostChange && onUpdateCostChange(e.target.checked)}
              />
              <span>تحديث كلفة المنتج من هذه الفاتورة</span>
            </label>
            <div className="text-[11px] text-ink-3 ps-5 -mt-1">
              {costPolicy === 'weighted'
                ? 'السياسة الحالية: «المتوسط المرجّح» — كلفة المنتج تُحسب متوسطاً بين المخزون القائم وكمية هذه الفاتورة.'
                : 'السياسة الحالية: «الكلفة الأخيرة المدخلة» — كلفة المنتج تصبح الكلفة المكتوبة في هذه الفاتورة.'}
              {' '}(تُضبط من الإعدادات › عام)
            </div>
            <label className="flex items-start gap-2 text-[11.5px] text-ink-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!updateSalePrice}
                onChange={(e) => onUpdateSalePriceChange && onUpdateSalePriceChange(e.target.checked)}
              />
              <span>تحديث سعر بيع المنتج إلى السعر المكتوب في الفاتورة</span>
            </label>
            <div className="text-[11px] text-ink-3">
              أسعار هذه الفاتورة تُحفَظ عليها في كل الحالات؛ هذان الخياران يمسّان بطاقة المنتج فقط.
            </div>
          </div>
        ) : null}

        {!readOnly ? (
          <div className="bg-paper-2 rounded-[10px] p-2.5 text-[11.5px] text-ink-2 flex items-start gap-2">
            <Icon name="clock" size={14} className="shrink-0 mt-0.5" />
            <div>الحفظ كمسوّدة لا يحرّك المخزون. «حفظ وترحيل» يحفظ ويرحّل فوراً: يزيد المخزون ويخصم من القاصة ويسجّل ذمة المورد.</div>
          </div>
        ) : null}

        {!readOnly ? (
          <div className="space-y-2 pt-1">
            <button
              type="button"
              className="btn w-full justify-center"
              onClick={onSaveDraft}
              disabled={saving || posting || items.length === 0 || cashboxMissing || insufficient
                || headDiscountTooBig || lineInvalid}
            >
              <Icon name="check" size={14} />
              {saving ? 'جاري الحفظ...' : (isEdit ? 'حفظ التعديلات' : 'حفظ كمسوّدة')}
            </button>
            <button
              type="button"
              className="btn btn-primary w-full justify-center"
              onClick={onSaveAndPost}
              disabled={saving || posting || items.length === 0 || !canPost}
            >
              <Icon name="check" size={14} />
              {posting ? 'جاري الترحيل...' : 'حفظ وترحيل المخزون'}
            </button>
            {onCancel ? (
              <button type="button" className="btn w-full justify-center" onClick={onCancel}>
                إلغاء
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
